import type { AtlasFeature, MapBeat, SemanticConnection } from '../types';
import { localeText } from '../locale';

export const mapBeats: MapBeat[] = [
  {
    id: 'beat-00', chapterId: '00', chapterIndex: -1, label: localeText('Маршрут жизни', 'Өмүр жолу'), era: '1939—2008',
    camera: { mode: 'world', bounds: [[20.2, 38.0], [77.3, 43.8]], padding: 38, transition: 'fit' },
    visibleFeatureIds: ['place-skopje', 'place-ankara', 'place-bishkek', 'place-karasuu'],
    activeFeatureIds: ['place-skopje', 'place-ankara', 'place-bishkek', 'place-karasuu'],
    connectionIds: ['conn-life-karasuu-bishkek', 'conn-bishkek-ankara', 'conn-ankara-skopje'],
    registerTitle: localeText('Кара‑Суу, Бишкек, Анкара и Скопье', 'Кара‑Суу, Бишкек, Анкара жана Скопье'),
    registerFeatureIds: ['place-karasuu', 'place-bishkek', 'place-ankara', 'place-skopje'],
  },
  {
    id: 'beat-02', chapterId: '02', chapterIndex: 1, label: localeText('Образование', 'Билим алуу'), era: localeText('1950-е—1967', '1950-жылдар—1967'),
    camera: { mode: 'region', bounds: [[71.65, 42.0], [79.0, 43.0]], padding: 54, transition: 'fit' },
    visibleFeatureIds: ['place-talas', 'place-karakol', 'place-bishkek', 'inst-przhevalsk-ped', 'inst-kgu'],
    activeFeatureIds: ['place-talas', 'inst-przhevalsk-ped', 'inst-kgu'],
    connectionIds: ['conn-education-talas-karakol', 'conn-education-karakol-frunze'],
    registerTitle: localeText('Талас, Пржевальск, Фрунзе', 'Талас, Пржевальск, Фрунзе'),
    registerFeatureIds: ['place-talas', 'inst-przhevalsk-ped', 'inst-kgu'],
  },
  {
    id: 'beat-08', chapterId: '08', chapterIndex: 6, label: localeText('Дипломатия', 'Дипломатия'), era: '2002—2005',
    camera: { mode: 'world', bounds: [[20.3, 38.4], [76.0, 44.0]], padding: 62, transition: 'fit' },
    visibleFeatureIds: ['place-bishkek', 'place-ankara', 'place-skopje', 'place-istanbul'],
    activeFeatureIds: ['place-bishkek', 'place-ankara', 'place-skopje'],
    connectionIds: ['conn-bishkek-ankara', 'conn-ankara-skopje'],
    registerTitle: localeText('Служба: Бишкек — Анкара — Скопье; Стамбул — место издания книги', 'Кызмат жолу: Бишкек — Анкара — Скопье; Стамбул — китеп басылган жер'),
    registerFeatureIds: ['place-bishkek', 'place-ankara', 'place-skopje', 'place-istanbul'],
  },
];

export const beatById: Readonly<Record<string, MapBeat>> = Object.fromEntries(
  mapBeats.map((beat) => [beat.id, beat]),
);

export const atlasFeatures: AtlasFeature[] = [
  {
    id: 'place-karasuu', label: 'Кара‑Суу', shortLabel: 'Кара‑Суу', kind: 'place', chapterIds: ['01', '09'],
    coordinates: [72.4731454, 42.4837931], precision: 'locality', status: 'confirmed',
    description: localeText(
      'Кара‑Суу — село, где Карыпкулов родился в 1939 году. Маркер обозначает населённый пункт и поставлен у современной школы его имени; дом рождения не установлен.',
      'Кара‑Суу — Карыпкулов 1939-жылы төрөлгөн айыл. Белги калктуу конушту көрсөтүп, азыр анын ысымындагы мектептин жанына коюлган; туулган үйү аныкталган эмес.',
    ),
    sourceIds: ['src-012', 'src-014', 'src-051', 'src-052'],
  },
  {
    id: 'place-talas', label: 'Талас', shortLabel: 'Талас', kind: 'place', chapterIds: ['01', '02', '05'],
    coordinates: [72.2427, 42.5228], precision: 'city', status: 'confirmed',
    description: localeText(
      'Областной центр, связанный с учебой в ПТУ № 6 и региональным контекстом «Манас-1000».',
      '№ 6 кесиптик-техникалык окуу жайдагы билим алуу жана «Манас-1000» программасынын аймактык иштери менен байланышкан облустук борбор.',
    ),
    sourceIds: ['src-003', 'src-006', 'src-012', 'src-014', 'src-032'],
  },
  {
    id: 'place-karakol', label: localeText('Пржевальск / Каракол', 'Пржевальск / Каракол'), shortLabel: 'Пржевальск', historicalLabel: 'Пржевальск', kind: 'place', chapterIds: ['02'],
    coordinates: [78.3936, 42.4907], precision: 'city', status: 'confirmed',
    description: localeText(
      'Город учебы в педагогическом институте; в период биографии назывался Пржевальском.',
      'Педагогикалык институтта окуган шаар; биографиядагы бул мезгилде Пржевальск деп аталган.',
    ),
    sourceIds: ['src-004', 'src-014'],
  },
  {
    id: 'place-bishkek', label: localeText('Фрунзе / Бишкек', 'Фрунзе / Бишкек'), shortLabel: 'Бишкек', historicalLabel: 'Фрунзе', kind: 'place', chapterIds: ['03', '04', '05', '06', '08', '09'],
    coordinates: [74.5698, 42.8746], precision: 'city', status: 'confirmed',
    description: localeText(
      'Одна физическая точка с историческим изменением имени. Здесь сосредоточены государственные, научные, издательские и вещательные институты.',
      'Аталышы тарыхый мезгилде өзгөргөн бир шаар. Мамлекеттик, илимий, басма жана телерадио мекемелери ушул жерде топтолгон.',
    ),
    sourceIds: ['src-008', 'src-012', 'src-041'],
  },
  {
    id: 'place-ankara', label: 'Анкара', shortLabel: localeText('Турция', 'Түркия'), kind: 'place', chapterIds: ['08'],
    coordinates: [32.8597, 39.9334], precision: 'city', status: 'confirmed',
    description: localeText(
      'Место дипломатической службы в качестве посла Кыргызской Республики в Турции.',
      'Кыргыз Республикасынын Түркиядагы элчиси катары дипломатиялык кызмат өтөгөн жер.',
    ),
    sourceIds: ['src-009', 'src-022'],
  },
  {
    id: 'place-istanbul', label: 'Стамбул', shortLabel: 'Стамбул', kind: 'publication', chapterIds: ['08'],
    coordinates: [28.9784, 41.0082], precision: 'city', status: 'confirmed',
    description: localeText(
      'Место издания книги «КТР: Испытание временем» в 2003 году; не доказательство поездки автора.',
      '«КТР: Испытание временем» китеби 2003-жылы басылган жер; бул маалымат автордун өзүнчө сапарын далилдебейт.',
    ),
    sourceIds: ['src-033'],
  },
  {
    id: 'place-skopje', label: 'Скопье', shortLabel: localeText('Северная Македония', 'Түндүк Македония'), kind: 'place', chapterIds: ['08'],
    coordinates: [21.4275, 41.9981], precision: 'city', status: 'confirmed',
    description: localeText(
      'Место вручения верительных грамот президенту Македонии 14 декабря 2004 года.',
      '2004-жылдын 14-декабрында Македониянын президентине ишеним грамоталары тапшырылган жер.',
    ),
    sourceIds: ['src-024'],
  },
  {
    id: 'inst-przhevalsk-ped', label: localeText('Пржевальский педагогический институт', 'Пржевальск педагогикалык институту'), shortLabel: localeText('Пединститут', 'Педагогикалык институт'), historicalLabel: localeText('Пржевальский педагогический институт', 'Пржевальск педагогикалык институту'), kind: 'institution', chapterIds: ['02'],
    coordinates: [78.3936, 42.4907], precision: 'city', status: 'probable',
    description: localeText(
      'Карыпкулов окончил институт в 1962 году и начал здесь преподавать. Маркер показывает Пржевальск / Каракол; корпус и адрес периода учёбы пока не установлены.',
      'Карыпкулов институтту 1962-жылы аяктап, ушул жерде сабак бере баштаган. Белги Пржевальск / Каракол шаарын көрсөтөт; ал окуган мезгилдеги корпус менен дарек азырынча аныкталган эмес.',
    ),
    sourceIds: ['src-004', 'src-014', 'web-iksu-contact', 'osm-iksu-building'],
  },
  {
    id: 'inst-kgu', label: localeText('Кыргызский государственный университет', 'Кыргыз мамлекеттик университети'), shortLabel: localeText('КГУ', 'КМУ'), historicalLabel: localeText('Кыргызский государственный университет', 'Кыргыз мамлекеттик университети'), kind: 'institution', chapterIds: ['02'], years: [1964, 1968],
    coordinates: [74.5698, 42.8746], precision: 'city', status: 'probable',
    description: localeText(
      'Карыпкулов проходил аспирантуру КГУ в 1964–1967 годах и преподавал философию в 1967–1968-м. Маркер показывает Фрунзе; корпус и адрес этого периода не подтверждены.',
      'Карыпкулов 1964–1967-жылдары КМУнун аспирантурасында окуп, 1967–1968-жылдары философиядан сабак берген. Белги Фрунзени көрсөтөт; ошол мезгилдеги корпус менен дарек ырасталган эмес.',
    ),
    sourceIds: ['src-003', 'src-004', 'src-012', 'src-014', 'web-knu-address'],
  },
];

export const featureById: Readonly<Record<string, AtlasFeature>> = Object.fromEntries(
  atlasFeatures.map((feature) => [feature.id, feature]),
);

export const semanticConnections: SemanticConnection[] = [
  {
    id: 'conn-life-karasuu-bishkek', from: 'place-karasuu', to: 'place-bishkek', meaning: 'documented-sequence', status: 'confirmed', renderGeometry: true,
    label: localeText('Кара‑Суу — место рождения; Фрунзе / Бишкек — центр последующей учёбы и работы', 'Кара‑Суу — туулган жер; Фрунзе / Бишкек — кийинки окуу жана ишинин борбору'),
  },
  {
    id: 'conn-education-talas-karakol', from: 'place-talas', to: 'place-karakol', meaning: 'documented-sequence', status: 'family', renderGeometry: true,
    label: localeText('Семейные материалы: учёба в Таласе и Пржевальске', 'Үй-бүлөлүк материалдар: Талас менен Пржевальскиде окуган жылдар'),
  },
  {
    id: 'conn-education-karakol-frunze', from: 'place-karakol', to: 'inst-kgu', meaning: 'documented-sequence', status: 'confirmed', renderGeometry: true,
    label: localeText('После Пржевальска — аспирантура и преподавание философии в КГУ во Фрунзе', 'Пржевальскиден кийин — Фрунзедеги КМУнун аспирантурасы жана философиядан сабак берүү'),
  },
  {
    id: 'conn-bishkek-ankara', from: 'place-bishkek', to: 'place-ankara', meaning: 'diplomatic-posting', status: 'confirmed', renderGeometry: true,
    label: localeText('Дипломатическое назначение в Турцию', 'Түркияга дипломатиялык кызматка дайындоо'),
  },
  {
    id: 'conn-ankara-skopje', from: 'place-ankara', to: 'place-skopje', meaning: 'diplomatic-accreditation', status: 'confirmed', renderGeometry: true,
    label: localeText('Аккредитация и вручение верительных грамот в Скопье', 'Скопьедеги аккредитация жана ишеним грамоталарын тапшыруу'),
  },
];

export const connectionById: Readonly<Record<string, SemanticConnection>> = Object.fromEntries(
  semanticConnections.map((connection) => [connection.id, connection]),
);
